import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { SiteNav } from "@/components/home/SiteNav";
import { SiteFooter } from "@/components/home/SiteFooter";
import { CONTACT_EMAIL, TPT_STORE_URL } from "@/data/storeLinks";

const UPDATED = "June 2026";

const PAGES = {
  "/privacy": {
    title: "Privacy Policy",
    intro: "Your privacy matters to us. This policy explains what information Embrace the Magic of Words collects and how it is used.",
    sections: [
      { h: "Information we collect", items: [
        "Newsletter and course-waitlist signups: your email address and, optionally, your name.",
        "Course members: email address, purchase records processed securely by Stripe, lesson progress and AI Tutor session history.",
        "Kids Corner: a child-chosen nickname, game scores and badges. Children are never asked for an email, real name, or other personal details. See our Children's Privacy page.",
        "Basic analytics (Google Analytics) about how the website is used, such as pages visited and device type.",
      ]},
      { h: "How we use it", items: [
        "To send the free worksheet, teaching tips, and news you asked for. You can unsubscribe at any time.",
        "To provide course access, track lesson completion, and generate your completion certificate.",
        "To save private game progress so a child can continue where they left off.",
        "To improve the website and fix problems.",
      ]},
      { h: "What we don't do", items: [
        "We never sell or rent personal information.",
        "We do not show advertising on this website or inside Kids Corner.",
        "We do not publish leaderboards or share any child's progress publicly.",
      ]},
      { h: "Third-party services", items: [
        "Payments: Stripe. Email delivery: Resend. AI features: OpenAI / Google models via secure API. Analytics: Google Analytics.",
        "Purchases made on Teachers Pay Teachers or Amazon are governed by those platforms' own privacy policies.",
      ]},
      { h: "Your choices", items: [
        `Email ${CONTACT_EMAIL} to access, correct, or delete your information. Parents can delete a child's Word Play profile and all progress from inside the app at any time.`,
      ]},
    ],
  },
  "/childrens-privacy": {
    title: "Children's Privacy",
    intro: "Kids Corner and the Word Play app are designed for children ages 5–10. We take children's privacy seriously and follow the principles of COPPA (the Children's Online Privacy Protection Act).",
    sections: [
      { h: "What children can enter", items: [
        "A nickname they choose (letters only). We ask kids never to use their real full name.",
        "Game answers, sentences, and short stories used only to play the activity and give encouraging feedback.",
      ]},
      { h: "What we never ask children for", items: [
        "Email address, real name, photo, location, school, phone number, or any other contact details.",
      ]},
      { h: "Parent-managed profiles (Word Play app)", items: [
        "A parent or guardian enters their own email to set up a child profile and can view or delete that profile — and all of its progress — at any time.",
      ]},
      { h: "No ads, no public sharing", items: [
        "There are no advertisements in Kids Corner. Points and badges stay private to the child and their grown-up. There is no public leaderboard or chat.",
      ]},
      { h: "AI features", items: [
        "Lumi's feedback and Story Magic use AI models to read a child's sentence or story and respond. Text is sent securely for processing and is not used to identify a child or to build a profile about them.",
      ]},
      { h: "Questions or deletion requests", items: [`Parents and guardians can contact ${CONTACT_EMAIL} and we will respond promptly.`] },
    ],
  },
  "/terms": {
    title: "Terms of Use",
    intro: "By using this website, Kids Corner, the Word Play app, or the course, you agree to these simple terms.",
    sections: [
      { h: "Use of the site", items: [
        "The website is for personal, family, classroom, and therapy use. Please don't copy, resell, or redistribute our materials.",
        "Children should use Kids Corner with a parent, guardian, or teacher's permission.",
      ]},
      { h: "Printables & paperbacks", items: [
        "Digital printables purchased on Teachers Pay Teachers are licensed for single-classroom or single-family use unless additional licenses are purchased there.",
        "Paperback workbooks sold on Amazon are subject to Amazon's terms of sale.",
      ]},
      { h: "Course & AI tools", items: [
        "Course access is for the purchasing individual. AI-generated activities and tutor responses are suggestions — please review them before using with a child.",
      ]},
      { h: "Disclaimer", items: [
        "Our resources support learning but are not a substitute for professional evaluation or therapy. Content is provided 'as is' without warranties of any kind.",
      ]},
      { h: "Changes", items: ["We may update these terms from time to time; the date above shows the latest revision."] },
    ],
  },
  "/refund-policy": {
    title: "Refund Policy",
    intro: "We want you to be happy with every resource. Here is how refunds work depending on where you purchased.",
    sections: [
      { h: "Teachers Pay Teachers printables", items: [
        "Refunds for TpT purchases are handled through TpT according to their refund policy. Please contact TpT support or email us and we'll help point you in the right direction.",
      ]},
      { h: "Amazon paperbacks", items: ["Returns and refunds for paperback workbooks follow Amazon's return policy for the marketplace where you purchased."] },
      { h: "Online course", items: [
        "The course is not yet available for purchase. A refund policy will be published here before enrollment opens.",
      ]},
      { h: "Free resources", items: ["Free worksheets, the AI Lesson Generator, Kids Corner, and the Word Play app are free — no purchase required."] },
      { h: "Need help?", items: [`Email ${CONTACT_EMAIL} and we'll do our best to make things right.`] },
    ],
  },
};

const ContactBody = () => (
  <div className="grid sm:grid-cols-2 gap-5" data-testid="contact-cards">
    <a href={`mailto:${CONTACT_EMAIL}`} className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 hover:-translate-y-0.5 transition-transform" data-testid="contact-email-link">
      <div className="w-12 h-12 bg-sky-200 border-2 border-foreground rounded-2xl flex items-center justify-center mb-3"><EnvelopeSimple size={26} weight="duotone" /></div>
      <h2 className="font-nunito font-extrabold tracking-tight text-xl">Email Ann</h2>
      <p className="font-nunito text-sm text-slate-600 mt-1 break-all">{CONTACT_EMAIL}</p>
      <p className="font-nunito text-sm text-slate-700 mt-3">Questions about a workbook, Kids Corner, the course, or your child's profile — I usually reply within two business days.</p>
    </a>
    <a href={TPT_STORE_URL} target="_blank" rel="noopener noreferrer" className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-6 hover:-translate-y-0.5 transition-transform" data-testid="contact-tpt-link">
      <div className="w-12 h-12 bg-amber-200 border-2 border-foreground rounded-2xl flex items-center justify-center mb-3 text-2xl">🛍️</div>
      <h2 className="font-nunito font-extrabold tracking-tight text-xl">Ask on TpT</h2>
      <p className="font-nunito text-sm text-slate-700 mt-3">Use the "Ask a question" button on any product page for help with a specific printable.</p>
    </a>
  </div>
);

export default function LegalPage() {
  const { pathname } = useLocation();
  const isContact = pathname === "/contact";
  const page = PAGES[pathname];

  useEffect(() => {
    document.title = `${isContact ? "Contact" : page?.title || "Info"} | Embrace the Magic of Words`;
    window.scrollTo(0, 0);
  }, [pathname, isContact, page]);

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <main className="max-w-3xl mx-auto px-4 py-12 sm:py-16" data-testid={`page-${isContact ? "contact" : pathname.slice(1)}`}>
        <a href="/" className="inline-flex items-center gap-1 font-nunito font-bold text-sm text-slate-600 hover:text-foreground mb-6" data-testid="legal-back-home">
          <ArrowLeft size={16} weight="bold" /> Back to home
        </a>
        <h1 className="font-nunito font-extrabold tracking-tight text-4xl sm:text-5xl text-foreground mb-3">{isContact ? "Contact" : page.title}</h1>
        <p className="font-nunito text-base sm:text-lg text-slate-600 mb-8">
          {isContact ? "I'd love to hear from you — whether you're a parent, teacher, therapist, or homeschooler." : page.intro}
        </p>
        {isContact ? (
          <ContactBody />
        ) : (
          <>
            <p className="font-nunito text-xs text-slate-500 mb-6">Last updated: {UPDATED}</p>
            <div className="space-y-7">
              {page.sections.map((s) => (
                <section key={s.h} className="bg-white border-2 border-foreground rounded-2xl shadow-brutal-sm p-6">
                  <h2 className="font-nunito font-extrabold tracking-tight text-xl text-foreground mb-3">{s.h}</h2>
                  <ul className="list-disc pl-5 space-y-2 font-nunito text-sm sm:text-base text-slate-700 leading-relaxed">
                    {s.items.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </section>
              ))}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
